import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Table,
  Spin,
  Select,
  Button,
  message,
  Drawer,
  Row,
  Col,
  Segmented,
} from "antd";
import { useNavigate } from "react-router-dom";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import axiosInstance from "../../../../config/axios.config";
import {
  getPackageProgressAPI,
  getPackagesAPI,
} from "../../../../services/outpatient.services";
import ServicesOpTable from "../../../Receptionist/Outpatients/OPServices/ServicesOpTable";
import AssignedOpServicesTable from "../../../Receptionist/Outpatients/OPServices/AssignedOpServicesTable";
import "./packagesOp.css";

const { Text, Title } = Typography;
const MOBILE_MEDIA = "(max-width: 768px)";

const PackagesOP = ({ facilityId: propFacilityId }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const admissionId = searchParams.get("aid");
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(MOBILE_MEDIA).matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MEDIA);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

useEffect(() => {
  if (admissionId) {
    const url = new URL(window.location.href);
    if (url.searchParams.get("admission") !== admissionId) {
      url.searchParams.set("admission", admissionId);
      navigate(`${url.pathname}?${url.searchParams.toString()}`, { replace: true });
    }
  }
}, [admissionId, navigate]);

  const patientId = searchParams.get("pid");
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState(null);
  const [packageList, setPackageList] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [fallbackMode, setFallbackMode] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [tabKey, setTabKey] = useState(0);
  const [activeTab, setActiveTab] = useState("package");
  const safeFacilityId =
    propFacilityId || searchParams.get("facility_id") || null;

  const [packageStatus, setPackageStatus] = useState(null);
  const [suggestedPackage, setSuggestedPackage] = useState(null);

  const fetchOutpatientPackage = async () => {
    if (!admissionId) {
      setLoading(false);
      setFallbackMode(true);
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/careservices/outpatient-packages/${admissionId}/`
      );

      const apiData = res?.data?.data;
      if (!apiData) {
        setFallbackMode(true);
        return;
      }

      if (apiData.status === "Ordered") {
        const progressRes = await getPackageProgressAPI(admissionId);
        if (progressRes?.data && progressRes.data.package) {
          setProgressData(progressRes.data);
          setPackageStatus("ordered");
          setFallbackMode(false);
        }
      } else if (apiData.status === "Suggested") {
        setPackageStatus("suggested");
        setSuggestedPackage(apiData.package || null);
        setFallbackMode(false);
      } else {
        setFallbackMode(true);
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setFallbackMode(true);
      } else {
        console.error("Error fetching outpatient package:", err);
        setFallbackMode(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    if (!safeFacilityId) return;
    setLoading(true);
    try {
      const res = await getPackagesAPI(safeFacilityId);
      const packages = Array.isArray(res?.data?.data) ? res.data.data : [];
      setPackageList(packages);
    } catch (err) {
      console.error("Error fetching packages:", err);
      setPackageList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPackage = async () => {
    if (!selectedPackage) {
      message.warning("Please select a package");
      return;
    }
    try {
      setLoading(true);
      const payload = {
        op_admission: admissionId,
        package: selectedPackage,
        discount: 0,
      };
      const res = await axiosInstance.post(
        "/careservices/op-suggest-package/",
        payload
      );

      if (res?.status === 200 || res?.status === 201) {
        message.success("Package assigned successfully!");
        setSelectedPackage(null);

        await fetchOutpatientPackage();
      } else {
        message.error("Failed to assign package");
      }
    } catch (err) {
      console.error("Error assigning package:", err);
      message.error("Error assigning package");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOutpatientPackage();
  }, [admissionId]);

  useEffect(() => {
    if (fallbackMode && safeFacilityId) {
      fetchPackages();
    }
  }, [fallbackMode, safeFacilityId]);

  const packageTabOptions = isMobile
    ? [
        { label: "Package", value: "package" },
        { label: "All Services", value: "all_services" },
        { label: "Assigned", value: "assigned_services" },
      ]
    : [
        { label: "Package", value: "package" },
        { label: "All Services", value: "all_services" },
        { label: "Assigned Services", value: "assigned_services" },
      ];
  if (loading) {
    return <Spin size="large" style={{ marginTop: 40, display: "block" }} />;
  }

  return (
    <div className="op-packages-root">
      <Row className="op-packages-tabs-row">
        <Col span={24}>
          <Segmented
            size="middle"
            block={isMobile}
            className="op-packages-segmented"
            options={packageTabOptions}
            value={activeTab}
            onChange={(val) => {
              setActiveTab(val);
              setTabKey((prev) => prev + 1);

              if (admissionId) {
                const url = new URL(window.location.href);
                url.searchParams.set("admission", admissionId);
                window.history.replaceState({}, "", url);
              }
            }}
          />
        </Col>
      </Row>
      {activeTab === "package" && (
        <>
          {packageStatus === "suggested" && suggestedPackage && (
            <Card>
              <Title level={4}>Suggested Package</Title>
              <Text strong style={{ fontSize: 16 }}>
                {suggestedPackage.name}
              </Text>
              <Text type="secondary" style={{ marginLeft: 10 }}>
                (Still reviewing)
              </Text>
            </Card>
          )}

          {packageStatus === "ordered" && progressData && (
            <Card>
              <Text strong className="op-packages-package-title">
                {progressData.package?.name || "N/A"}
              </Text>
              <Table
                className="op-packages-progress-table"
                dataSource={progressData.data.map((s, i) => ({ key: i, ...s }))}
                  columns={[
                    {
                      title: "Service Name",
                      dataIndex: "name",
                      align: "left",
                      className: "op-packages-service-name-col",
                      onHeaderCell: () => ({
                        className: "op-packages-service-name-col",
                        style: { textAlign: "left" },
                      }),
                      onCell: () => ({
                        className: "op-packages-service-name-col",
                        style: { textAlign: "left" },
                      }),
                      render: (name) => (
                        <span className="op-packages-service-name-text">
                          {name}
                        </span>
                      ),
                    },
                    {
                      title: "Progress",
                      align: "center",
                      className: "op-packages-progress-col",
                      onHeaderCell: () => ({
                        className: "op-packages-progress-col",
                      }),
                      onCell: () => ({
                        className: "op-packages-progress-col",
                      }),
                      render: (_, record) => (
                        <span className="op-packages-progress-value">
                          {record.processed_count}/{record.actual_count}
                        </span>
                      ),
                    },
                    {
                      title: "Details",
                      align: "center",
                      render: (_, record) =>
                        record.sessions_info?.length > 0 ? (
                          <Typography.Link
                            onClick={() => {
                              navigate(
                                `/op/home?pid=${patientId}&aid=${admissionId}&status=all&service=physio-care&admission=${admissionId}&pkg_service=${encodeURIComponent(
                                  record.name
                                )}`
                              );
                            }}
                          >
                            View
                          </Typography.Link>
                        ) : (
                          <Text type="secondary">—</Text>
                        ),
                    },
                  ]}

                pagination={false}
                expandable={{
                  columnWidth: 24,
                  expandIcon: ({ expanded, onExpand, record }) =>
                    record.sessions_info?.length ? (
                      expanded ? (
                        <UpOutlined
                          style={{ fontSize: 14, cursor: "pointer" }}
                          onClick={(e) => onExpand(record, e)}
                        />
                      ) : (
                        <DownOutlined
                          style={{ fontSize: 14, cursor: "pointer" }}
                          onClick={(e) => onExpand(record, e)}
                        />
                      )
                    ) : (
                      <span style={{ display: "inline-block", width: 14 }} />
                    ),

                 expandedRowRender: (record) => (
                   <div style={{ paddingLeft: 10 }}>
                     <Table
                       bordered
                       pagination={false}
                       dataSource={(record.sessions_info || []).map((session, index) => ({
                         key: index,
                         date: new Date(session.session_date).toLocaleDateString("en-GB", {
                           day: "2-digit",
                           month: "short",
                           year: "numeric",
                         }),
                         therapist: `${session.done_by_first_name || ""} ${session.done_by_last_name || ""}`,
                         session: session.session,
                       }))}
                       columns={[
                         { title: "Session", dataIndex: "session" },
                         { title: "Date of Session", dataIndex: "date" },
                         { title: "Therapist Name", dataIndex: "therapist" },
                       ]}
                       locale={{ emptyText: "No sessions recorded yet" }}
                     />
                   </div>
                 ),
               
                 rowExpandable: (record) => record.sessions_info?.length > 0,
               }}

              />

            </Card>
          )}

          {fallbackMode && (
            <Card>
              <Title level={4}>Assign a Package</Title>

              {(() => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center",
        gap: isMobile ? 8 : 10,
        marginBottom: "20px",
        width: "100%",
        
      }}
    >
      <Select
        placeholder="Select Package"
        style={{
          width: isMobile ? "100%" : 250,
          minWidth: 200,
        }}
        value={selectedPackage}
        onChange={(val) => setSelectedPackage(val)}
        placement="bottomLeft"
      >
        {(packageList || []).map((pkg) => (
          <Select.Option key={pkg.id} value={pkg.id}>
            {pkg.name}
          </Select.Option>
        ))}
      </Select>

      <Button
        type="primary"
        onClick={handleAssignPackage}
        style={{
          width: isMobile ? "100%" : "auto",
          fontWeight: 600,
          padding: isMobile ? "6px 0" : "0 10px",
        }}
      >
        Submit
      </Button>
    </div>
  );
})()}


              {selectedPackage && (
                <div
                  style={{
                    marginBottom: 20,
                    padding: 10,
                    border: "1px solid #e0e0e0",
                    borderRadius: 6,
                    display: "flex",
                    gap: 20,
                    width: "fit-content",
                  }}
                >
                  {packageList.find((p) => p.id === selectedPackage)?.no_of_days > 0 && (
                    <div>
                      <Text strong>No. of Days: </Text>
                      <Text>
                        {packageList.find((p) => p.id === selectedPackage)?.no_of_days}
                      </Text>
                    </div>
                  )}

                  <div>
                    <Text strong>Total Price: </Text>
                    <Text>
                      ₹
                      {packageList.find((p) => p.id === selectedPackage)
                        ?.total_price || 0}
                    </Text>
                  </div>
                </div>
              )}

              {packageList.length > 0 && selectedPackage && (
  <Table
    dataSource={
      packageList
        .filter((pkg) => pkg.id === selectedPackage)
        .flatMap((pkg, i) =>
          (pkg.package_services || []).map((srv, idx) => ({
            key: `${i}-${idx}`,
            service_name: srv.service?.name || "N/A",
            quantity: srv.quantity,
          }))
        )
    }
    columns={[
      { title: "Service", dataIndex: "service_name" },
      { title: "Quantity", dataIndex: "quantity" },
    ]}
    pagination={false}
  />
)}
            </Card>
          )}
          <Drawer
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text strong>{selectedService?.name}</Text>
                {selectedService && (
                  <span
                    style={{
                      background: "#f0f0f0",
                      padding: "2px 8px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {selectedService.processed_count}/
                    {selectedService.actual_count}
                  </span>
                )}
              </div>
            }
            placement="right"
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            width={400}
          >
            {selectedService?.sessions_info?.length ? (
              selectedService.sessions_info.map((session, idx) => (
                <div key={idx} style={{ marginBottom: "15px" }}>
                  <p>
                    <Text strong>Date of session: </Text>
                    {new Date(session.session_date).toLocaleDateString(
                      "en-GB",
                      {
                        day: "2-digit",
                        month: "short",
                        year: "2-digit",
                      }
                    )}
                  </p>
                  <p>
                    <Text strong>Therapist name: </Text>
                    {session.done_by_first_name} {session.done_by_last_name}
                  </p>
                  <p>
                    <Text strong>Session: </Text>
                    {session.session}
                  </p>
                  {idx !== selectedService.sessions_info.length - 1 && (
                    <hr style={{ border: "0.5px solid #f0f0f0" }} />
                  )}
                </div>
              ))
            ) : (
              <Text type="secondary">No sessions recorded yet.</Text>
            )}
          </Drawer>
        </>
      )}

{activeTab === "all_services" && (
  <Card>
    <ServicesOpTable admissionId={admissionId} key={admissionId} />
  </Card>
)}

{activeTab === "assigned_services" && (
  <Card>
    <AssignedOpServicesTable admissionId={admissionId} key={admissionId} />
  </Card>
)}

    </div>
  );
};

export default PackagesOP;
